import React, { useEffect } from 'react';
import Router from './components/Router';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import fonts from './components/theme/fonts';
import DoneeLoader from './providers/DoneeLoader';
import { BrowserRouter } from 'react-router-dom';
import DonorProvider from './providers/DonorProvider/DonorProvider';
import { colors } from './components/theme';
import PushEventsProvider from './hooks/PushEvents';
import LoginToastProvider from './providers/LoginToastProvider';
import { LoginToast } from './components';
import { Donor } from './providers/DonorProvider/interfaces';
import { LocalStorageCart } from './components/Donee/components/CheckoutWizard/CartLocalStorage';
import LoginBottomSheet from './components/LoginBottomSheet';
import LastLoginProvider from './components/LoginBottomSheet/LastLoginProvider';
import PaymentOutageProvider from './providers/PaymentOutageProvider';
import PaymentOutage from './components/SomethingWentWrong/PaymentOutage';
import { FeatureFlagProvider } from './providers';
import { DonorWithWallet } from './providers/DonorProvider/interfaces';
import { QueryClientProvider } from '@tanstack/react-query';
import { appQueryClient, useDonorProfile } from './api';

const rootElement = document.getElementById('root');

export const theme = createTheme({
    typography: {
        fontFamily: fonts.fontfamily,
    },
    palette: {
        primary: {
            main: colors.primaryOrange,
        },
        secondary: {
            main: colors.primaryGuidingViolet,
        },
    },
    components: {
        MuiPopover: { defaultProps: { container: rootElement } },
        MuiDialog: { defaultProps: { container: rootElement } },
        MuiModal: { defaultProps: { container: rootElement } },
    },
});

type FBQCommand = 'track' | 'trackCustom' | 'init';

interface FBQPayload {
    [key: string]: any;
}

declare global {
    interface Window {
        version?: string;
        cart: LocalStorageCart;
        opera: any;
        MSStream: any;
        accessToken?: string;
        setLogLevel?: () => unknown;
        fbq?: (
            command: FBQCommand,
            event: string,
            payload?: FBQPayload,
        ) => unknown;
        clevertap?: {
            setLogLevel?: () => unknown;
            profile?: {
                Site: { donor_name: string; Email: string; Identity: string };
            }[];
        };
    }
}

// Lifts the donor profile out of the query cache, which only exists inside
// QueryClientProvider, up to the feature flag provider that sits above it.
const DonorSessionSync: React.FC<{
    onDonorSessionChange: (donorSession: DonorWithWallet | undefined) => void;
}> = ({ onDonorSessionChange }) => {
    const { donorProfile } = useDonorProfile();

    useEffect(() => {
        onDonorSessionChange(donorProfile);
    }, [donorProfile, onDonorSessionChange]);

    return null;
};

const App: React.FC = () => {
    const donorEmailRef = React.useRef<Donor | undefined>(undefined);
    const [donorSession, setDonorSession] = React.useState<
        DonorWithWallet | undefined
    >(undefined);

    return (
        <BrowserRouter
            basename="/donate"
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <FeatureFlagProvider donorSession={donorSession}>
                <QueryClientProvider client={appQueryClient}>
                    <PushEventsProvider donorEmailRef={donorEmailRef}>
                        <DoneeLoader>
                            <DonorSessionSync
                                onDonorSessionChange={setDonorSession}
                            />
                            <DonorProvider donorEmailRef={donorEmailRef}>
                                <ThemeProvider theme={theme}>
                                    <LastLoginProvider>
                                        <LoginToastProvider>
                                            <PaymentOutageProvider>
                                                <PaymentOutage />
                                                <Router />
                                                <LoginToast />
                                                <LoginBottomSheet />
                                            </PaymentOutageProvider>
                                        </LoginToastProvider>
                                    </LastLoginProvider>
                                </ThemeProvider>
                            </DonorProvider>
                        </DoneeLoader>
                    </PushEventsProvider>
                </QueryClientProvider>
            </FeatureFlagProvider>
        </BrowserRouter>
    );
};

export default App;
